import {
  AbstractMesh,
  Animation,
  AnimationGroup,
  Color3,
  Mesh,
  Nullable,
  Scene,
  StandardMaterial,
  Texture,
} from '@babylonjs/core'
import { BuildingElementType } from '../../models/Construction'
import { SceneManager } from '../sceneManager'
import {
  BuildingStructuralTypes,
  StructuralBackgroundColor,
} from '../../models/Structural'
import { getEnums } from '../../utils/Common'
import GridPluginMaterial from './gridPluginMaterial'
import store from '../../store'
import { StructuralAnimationStatus } from '../../reducers/structural'
import { GridMaterial } from '@babylonjs/materials'

const groundNormalMaterialName = 'Ground Normal'
const wallNormalMaterialName = 'Wall Normal'
const doorNormalMaterialName = 'Door Normal'
const windowNormalMaterialName = 'Window Normal'
const roofNormalMaterialName = 'Roof Normal'

const groundHighlightMaterialName = 'Ground Highlight'
const wallHighlightMaterialName = 'Wall Highlight'
const doorHighlightMaterialName = 'Door Highlight'
const windowHighlightMaterialName = 'Window Highlight'
const roofHighlightMaterialName = 'Roof Highlight'

const groundInactiveMaterialName = 'Ground Inactive'
const wallInactiveMaterialName = 'Wall Inactive'
const doorInactiveMaterialName = 'Door Inactive'
const windowInactiveaterialName = 'Window Inactive'
const roofInactiveMaterialName = 'Roof Inactive'

const groundCriticalMaterialName = 'Ground Critical'
const wallCriticalMaterialName = 'Wall Critical'
const doorCriticalMaterialName = 'Door Critical'
const windowCriticalMaterialName = 'Window Critical'
const roofCriticalMaterialName = 'Roof Critical'

const groundAcceptedMaterialName = 'Ground Accepted'
const wallAcceptedMaterialName = 'Wall Accepted'
const doorAcceptedMaterialName = 'Door Accepted'
const windowAcceptedMaterialName = 'Window Accepted'
const roofAcceptedMaterialName = 'Roof Accepted'

export class MaterialManager {
  private static instance: MaterialManager | null
  private constructionMaterials: Map<string, StandardMaterial> = new Map()
  private scene?: Scene

  private constructor() {
    this.scene = SceneManager.getInstance().getScene()
  }

  static getInstance(): MaterialManager {
    if (!MaterialManager.instance) {
      MaterialManager.instance = new MaterialManager()
    }
    return MaterialManager.instance
  }

  isMaterialShared(mesh: AbstractMesh) {
    return (
      mesh.material &&
      mesh.material
        .getBindedMeshes()
        .filter((m) => m.uniqueId !== mesh.uniqueId).length
    )
  }

  createStandardMaterial(
    name: string,
    scene: Scene | undefined,
    options?: {
      diffuseColor?: Color3
      specularColor?: Color3
      emissiveColor?: Color3
      diffuseTexture?: Texture
      alpha?: number
    },
  ): StandardMaterial {
    if (this.constructionMaterials.has(name)) {
      return this.constructionMaterials.get(name) as StandardMaterial
    }

    const material = new StandardMaterial(name, scene)
    if (options?.diffuseColor) material.diffuseColor = options.diffuseColor
    if (options?.specularColor) material.specularColor = options.specularColor
    if (options?.emissiveColor) material.emissiveColor = options.emissiveColor
    if (options?.diffuseTexture)
      material.diffuseTexture = options.diffuseTexture
    if (options?.alpha !== undefined) material.alpha = options.alpha
    material.sideOrientation = Mesh.DOUBLESIDE
    material.backFaceCulling = false

    this.constructionMaterials.set(name, material)
    return material
  }

  getMaterial(name: string): Nullable<StandardMaterial> {
    const material = this.constructionMaterials.get(name)
    return material ? material : null
  }

  getNormalMaterial(elementType: BuildingElementType) {
    let materialName = ''
    switch (elementType) {
      case BuildingElementType.Floor:
        materialName = groundNormalMaterialName
        break
      case BuildingElementType.Wall:
        materialName = wallNormalMaterialName
        break
      case BuildingElementType.Door:
        materialName = doorNormalMaterialName
        break
      case BuildingElementType.Window:
        materialName = windowNormalMaterialName
        break
      case BuildingElementType.Roof:
        materialName = roofNormalMaterialName
        break
    }

    return this.getMaterial(materialName)
  }

  getHighlightMaterial(elementType: BuildingElementType) {
    let materialName = ''
    switch (elementType) {
      case BuildingElementType.Floor:
        materialName = groundHighlightMaterialName
        break
      case BuildingElementType.Wall:
        materialName = wallHighlightMaterialName
        break
      case BuildingElementType.Door:
        materialName = doorHighlightMaterialName
        break
      case BuildingElementType.Window:
        materialName = windowHighlightMaterialName
        break
      case BuildingElementType.Roof:
        materialName = roofHighlightMaterialName
        break
    }

    return this.getMaterial(materialName)
  }

  getCriticalMaterial(elementType: BuildingElementType) {
    let materialName = ''
    switch (elementType) {
      case BuildingElementType.Floor:
        materialName = groundCriticalMaterialName
        break
      case BuildingElementType.Wall:
        materialName = wallCriticalMaterialName
        break
      case BuildingElementType.Door:
        materialName = doorCriticalMaterialName
        break
      case BuildingElementType.Window:
        materialName = windowCriticalMaterialName
        break
      case BuildingElementType.Roof:
        materialName = roofCriticalMaterialName
        break
    }

    return this.getMaterial(materialName)
  }

  getAcceptedMaterial(elementType: BuildingElementType) {
    let materialName = ''
    switch (elementType) {
      case BuildingElementType.Floor:
        materialName = groundAcceptedMaterialName
        break
      case BuildingElementType.Wall:
        materialName = wallAcceptedMaterialName
        break
      case BuildingElementType.Door:
        materialName = doorAcceptedMaterialName
        break
      case BuildingElementType.Window:
        materialName = windowAcceptedMaterialName
        break
      case BuildingElementType.Roof:
        materialName = roofAcceptedMaterialName
        break
    }

    return this.getMaterial(materialName)
  }

  getInactiveMaterial(elementType: BuildingElementType) {
    let materialName = ''
    switch (elementType) {
      case BuildingElementType.Floor:
        materialName = groundInactiveMaterialName
        break
      case BuildingElementType.Wall:
        materialName = wallInactiveMaterialName
        break
      case BuildingElementType.Door:
        materialName = doorInactiveMaterialName
        break
      case BuildingElementType.Window:
        materialName = windowInactiveaterialName
        break
      case BuildingElementType.Roof:
        materialName = roofInactiveMaterialName
        break
    }

    return this.getMaterial(materialName)
  }

  getStructuralMaterial(type: BuildingStructuralTypes) {
    const material = this.getMaterial(
      `${type.split(' ').join('')}StructuralMaterial`,
    )
    return material ? material : null
  }

  getGridMaterial() {
    return this.scene
      ? (this.scene.getMaterialByName('gridMaterial') as GridMaterial)
      : null
  }

  getTransparentMaterial() {
    return this.scene ? this.scene.getMaterialByName('Transparent') : null
  }

  createConstructionMaterials() {
    this.createStandardMaterial(groundNormalMaterialName, this.scene, {
      diffuseColor: Color3.White(),
      specularColor: Color3.Black(),
      diffuseTexture: new Texture('/images/floorTexture.png'),
    })
    this.createStandardMaterial(wallNormalMaterialName, this.scene, {
      diffuseColor: Color3.White(),
      specularColor: Color3.Black(),
    })
    this.createStandardMaterial(doorNormalMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#808080'),
    })
    this.createStandardMaterial(windowNormalMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#808080'),
    })
    this.createStandardMaterial(roofNormalMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#C8C8C8'),
      specularColor: Color3.Black(),
    }) //.wireframe = true
    this.createStandardMaterial(groundHighlightMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#990000'),
    })
    const wallHighlightMaterial = this.createStandardMaterial(
      wallHighlightMaterialName,
      this.scene,
      {
        diffuseColor: Color3.FromHexString('#3F69FF'),
      },
    )
    wallHighlightMaterial.metadata = {
      overrideTransparency: true,
    }

    const semitransparency = 0.5
    this.createStandardMaterial(doorHighlightMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#0080FF'),
      alpha: semitransparency,
    })
    this.createStandardMaterial(windowHighlightMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#0080FF'),
      alpha: semitransparency,
    })
    this.createStandardMaterial(roofHighlightMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#223b05'),
    })

    this.createStandardMaterial(groundInactiveMaterialName, this.scene, {
      diffuseColor: Color3.Black(),
      alpha: semitransparency,
    })
    this.createStandardMaterial(wallInactiveMaterialName, this.scene, {
      diffuseColor: Color3.White(),
      specularColor: Color3.Black(),
      alpha: semitransparency,
    })

    this.createStandardMaterial(doorInactiveMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#808080'),
      alpha: semitransparency,
    })

    this.createStandardMaterial(windowInactiveaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#808080'),
      alpha: semitransparency,
    })

    this.createStandardMaterial(roofInactiveMaterialName, this.scene, {
      diffuseColor: Color3.FromHexString('#999999'),
      specularColor: Color3.Black(),
      alpha: semitransparency,
    })

    this.createStandardMaterial('Transparent', this.scene, { alpha: 0 })
  }

  createValidationMaterials() {
    this.createStandardMaterial(groundAcceptedMaterialName, this.scene, {
      diffuseColor: Color3.Green(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(wallAcceptedMaterialName, this.scene, {
      diffuseColor: Color3.Green(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(doorAcceptedMaterialName, this.scene, {
      diffuseColor: Color3.Green(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(windowAcceptedMaterialName, this.scene, {
      diffuseColor: Color3.Green(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(roofAcceptedMaterialName, this.scene, {
      diffuseColor: Color3.Green(),
      // alpha: semitransparency,
    })

    this.createStandardMaterial(groundCriticalMaterialName, this.scene, {
      diffuseColor: Color3.Red(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(wallCriticalMaterialName, this.scene, {
      diffuseColor: Color3.Red(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(doorCriticalMaterialName, this.scene, {
      diffuseColor: Color3.Red(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(windowCriticalMaterialName, this.scene, {
      diffuseColor: Color3.Red(),
      // alpha: semitransparency,
    })
    this.createStandardMaterial(roofCriticalMaterialName, this.scene, {
      diffuseColor: Color3.Red(),
      // alpha: semitransparency,
    })
  }

  createStructuralMaterials() {
    const alpha =
      store.getState().structural.structuralAnimationStatus ===
      StructuralAnimationStatus.Pending
        ? 0.0
        : 1.0
    const frameRate = 10
    const fadeAnimation = new Animation(
      'fadeAnimation',
      `alpha`,
      frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CYCLE,
    )
    fadeAnimation.setKeys([
      {
        frame: 0,
        value: 0,
      },
      {
        frame: frameRate,
        value: 1,
      },
    ])

    const structuralTypes = getEnums(BuildingStructuralTypes)
    structuralTypes.forEach(([value, type]) => {
      const structuralMaterial = this.createStandardMaterial(
        `${value}StructuralMaterial`,
        this.scene,
        {
          diffuseColor: Color3.FromHexString(StructuralBackgroundColor[type]),
          alpha,
        },
      )

      switch (type) {
        case BuildingStructuralTypes.ShearWall:
          structuralMaterial.zOffset = -3.0
          structuralMaterial.zOffsetUnits = -10.0
          break
        case BuildingStructuralTypes.LoadBearingWall:
          structuralMaterial.zOffset = -1.0
          structuralMaterial.zOffsetUnits = -10.0
          break
        case BuildingStructuralTypes.Joist:
          structuralMaterial.zOffset = 0.2
          structuralMaterial.zOffsetUnits = 10.0
          break
        case BuildingStructuralTypes.Beam:
          structuralMaterial.zOffset = 0.1
          structuralMaterial.zOffsetUnits = 5.0
          break
        case BuildingStructuralTypes.Stud:
          structuralMaterial.zOffset = 2.0
          structuralMaterial.zOffsetUnits = 10.0
          break
        case BuildingStructuralTypes.Header:
          structuralMaterial.zOffset = -5.0
          structuralMaterial.zOffsetUnits = -10.0
          break
      }

      structuralMaterial.metadata = {
        type: BuildingElementType.Structural,
        structuralType: type,
      }

      if (
        type === BuildingStructuralTypes.ShearWall ||
        type === BuildingStructuralTypes.LoadBearingWall
      ) {
        const gridPluginMaterial = new GridPluginMaterial(structuralMaterial)
        gridPluginMaterial.isEnabled = true

        if (type === BuildingStructuralTypes.ShearWall) {
          gridPluginMaterial.axisVisibility.set(0, 1, 0)
          gridPluginMaterial.minorUnitFrequency = 0
        }

        if (type === BuildingStructuralTypes.LoadBearingWall) {
          gridPluginMaterial.axisVisibility.set(1, 0, 1)
          gridPluginMaterial.minorUnitFrequency = 0
        }
      }

      const animationGroup = new AnimationGroup(
        `${value}FadeAnimationGroup`,
        this.scene,
      )
      animationGroup.speedRatio = 0.2

      animationGroup.addTargetedAnimation(fadeAnimation, structuralMaterial)

      animationGroup.metadata = {
        type: BuildingElementType.Structural,
        structuralType: type,
      }
    })
  }

  createGridMaterial() {
    const gridSettings = store.getState().construction.gridSettings
    const groundMaterial = new GridMaterial('gridMaterial', this.scene)
    const color = Color3.FromHexString(gridSettings.color)
    groundMaterial.majorUnitFrequency = gridSettings.cellFrequency
    groundMaterial.gridRatio = gridSettings.cellSize
    groundMaterial.backFaceCulling = false
    groundMaterial.mainColor = color
    groundMaterial.lineColor = color
    groundMaterial.opacity = 0.99
  }

  updateGridMaterial() {
    const gridSettings = store.getState().construction.gridSettings
    const groundMaterial = this.scene?.getMaterialByName(
      'gridMaterial',
    ) as GridMaterial
    if (groundMaterial) {
      const color = Color3.FromHexString(gridSettings.color)
      groundMaterial.majorUnitFrequency = gridSettings.cellFrequency
      groundMaterial.gridRatio = gridSettings.cellSize
      groundMaterial.backFaceCulling = false
      groundMaterial.mainColor = color
      groundMaterial.lineColor = color
    }
  }

  removeMaterial(name: string): boolean {
    this.constructionMaterials.get(name)?.dispose()
    return this.constructionMaterials.delete(name)
  }

  dispose() {
    this.constructionMaterials.forEach((material) => material.dispose())
    this.constructionMaterials.clear()

    MaterialManager.instance = null
  }
}
